import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useGuidance } from "../contexts/GuidanceContext";

interface Clause {
  uuid: string;
  standardCode: string;
  versionYear: number;
  clauseReference: string;
  clauseTitle: string;
  paraphrasedSummary: string;
  guidanceTipsUk: string | null;
}

interface EvidenceSummary {
  uuid: string;
  documentTitle: string;
  status: "Draft" | "InReview" | "Authorised" | "Archived";
  fileCount: number;
  updatedAt: string;
}

const formatDateUk = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const STATUS_TONE: Record<EvidenceSummary["status"], string> = {
  Draft: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  InReview: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  Authorised: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
  Archived: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

export function Clause() {
  const { slug, code, year, ref } = useParams<{
    slug: string;
    code: string;
    year: string;
    ref: string;
  }>();
  const { isGuidanceActive } = useGuidance();
  const [clause, setClause] = useState<Clause | null>(null);
  const [evidence, setEvidence] = useState<EvidenceSummary[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!code || !year || !ref) return;
    Promise.all([
      api<{ clause: Clause }>(`/standards/${code}/${year}/clauses/${encodeURIComponent(ref)}`),
      api<{ clause: Clause }>(`/standards/${code}/${year}/clauses/${encodeURIComponent(ref)}`).then(
        (c) => api<{ evidence: EvidenceSummary[] }>(`/evidence?requirementUuid=${c.clause.uuid}`),
      ),
    ])
      .then(([clauseRes, evidenceRes]) => {
        if (cancelled) return;
        setClause(clauseRes.clause);
        setEvidence(evidenceRes.evidence);
      })
      .catch((err) => {
        if (!cancelled) setErrors(formatApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [code, year, ref]);

  if (errors.length > 0) {
    return (
      <div
        role="alert"
        className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
      >
        {errors[0]}
      </div>
    );
  }

  if (!clause) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  }

  const standardLabel = `${clause.standardCode.replace(/^ISO/, "ISO ")}:${clause.versionYear}`;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/o/${slug}/standards/${clause.standardCode}/${clause.versionYear}`}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← {standardLabel} clauses
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          <span className="mr-2 text-slate-400">{clause.clauseReference}</span>
          {clause.clauseTitle}
        </h1>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Paraphrased summary
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-800 dark:text-slate-100">
          {clause.paraphrasedSummary}
        </p>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-500">
          Paraphrased in our own words; the original ISO text is copyrighted and not reproduced
          here.
        </p>
      </section>

      {isGuidanceActive && clause.guidanceTipsUk ? (
        <section className="rounded-lg border border-brand-200 bg-brand-50 p-4 dark:border-brand-900/40 dark:bg-brand-500/10">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-200">
            Coach
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-brand-900 dark:text-brand-100">
            {clause.guidanceTipsUk}
          </p>
        </section>
      ) : null}

      <section>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Evidence linked to this clause
          </h2>
          <Link
            to={`/o/${slug}/vault/new?requirementUuid=${clause.uuid}`}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
          >
            Add evidence
          </Link>
        </div>
        {evidence === null ? (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : evidence.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
            No evidence yet. Add a document and link it to this clause.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800">
            {evidence.map((e) => (
              <li key={e.uuid}>
                <Link
                  to={`/o/${slug}/vault/${e.uuid}`}
                  className="block p-3 hover:bg-slate-50 dark:hover:bg-slate-700/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {e.documentTitle}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {e.fileCount} file{e.fileCount === 1 ? "" : "s"} · updated{" "}
                        {formatDateUk(e.updatedAt)}
                      </p>
                    </div>
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_TONE[e.status]}`}
                    >
                      {e.status}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
